import {
  CoreStatusExtended,
  CourseLevelOfferStatus,
  IntakeStatus,
  NotReadyReason,
  OfferStatus,
  VisaStatus,
} from './enums.js';
import {
  ICourse,
  ITag,
  PartnerVisaStatus,
  EventInfoStatus,
  IImportCourse,
  RecordedInterviewSystemStatus,
} from '../index.js';
import { IAgent } from '../agent/index.js';

export interface IOffer {
  intakeId: string;
  intakeLabel?: string;
  intakeStatus?: IntakeStatus;
  offerId: string;
  agentName?: null | string;
  agentEmail?: null | string;
  assignedAgents?: Array<IAgent>;
  lockAgent?: boolean;
  lastAgentAssignmentDate?: string;
  isActive?: boolean;
  isCourseConflict?: boolean;
  courseReleaseEmailPending?: boolean;
  status?: CoreStatusExtended;
  previousStatus?: CoreStatusExtended;
  manualStatusChange?: CoreStatusExtended;
  notReadyReason?: NotReadyReason;
  visaStatus?: VisaStatus;
  regionConfigId?: string;
  /**
   * Stores all student tags. Including those that were deleted.
   * Deleted tags has a property `manualyRemoved` set to true.
   */
  tags?: ITag[];
  offerStatus?: OfferStatus;
  offerStatusByUni?: OfferStatus;
  courseLevelOfferStatuses?: CourseLevelOfferStatus[];
  isConditionalDocumentsUploaded?: boolean;
  startDate?: string;
  courses?: Array<ICourse>;
  visaSupport?: boolean;
  casReceived?: boolean;
  hasDependents?: boolean;
  hasPartner?: boolean;
  partnerVisaStatus?: PartnerVisaStatus;
  hasChildren?: boolean;
  childrenNum?: number;
  visaSupportLocked?: boolean;
  courseFeePaidToDate?: number;
  calendlyInterviewsInfoStatus?: EventInfoStatus;
  recordedInterviewSystemStatus?: RecordedInterviewSystemStatus;
  recordedInterviewStudentStatus?: RecordedInterviewSystemStatus;
  casBypass?: boolean;
  hasViewedBypassWelcomeMessage?: boolean;
  offersDurationMonths?: number;
  isPIPage1Complete?: boolean;
  isPIPage2Complete?: boolean;
  isPIPage3Complete?: boolean;
  isPIPage4Complete?: boolean;
  isPIPage5Complete?: boolean;
  isPIPage5_1Complete?: boolean;
  isPIPage6Complete?: boolean;
  isPIPage7Complete?: boolean;
  isPIPage8Complete?: boolean;
  isPIPage9Complete?: boolean;
  isPIPage10Complete?: boolean;
  isPISectionStarted?: boolean;
  isPISectionComplete?: boolean;

  isHSectionStarted?: boolean;
  isHSectionComplete?: boolean;
  isHPage1Complete?: boolean;
  isHPage2Complete?: boolean;
  isHPage3Complete?: boolean;
  externalCasStatus?: string;
  CASIssuedAutoMoved?: boolean;
  manualStatusChangeToPreDeposit?: CoreStatusExtended;
  isAgentConfirmed?: boolean;
  isAgentDetailsConfirmed?: boolean;
  withAgent?: boolean;
  branchOffice?: string | null;
  counsellorEmail?: string | null;
  counsellorName?: string | null;
  agentFamilyName?: string | null;
  agentGivenName?: string | null;
  branchAddressLine1?: string | null;
  branchAddressLine2?: string | null;
  branchAddressLine3?: string | null;
  branchCity?: string | null;
  branchCountry?: string | null;
  branchPostcode?: string | null;
  importAgentName?: string | null;
  importAgentEmail?: string | null;
  importAgentId?: string | null;
}

export const CLEARED_AGENT_DETAIL_FIELDS = {
  branchOffice: null,
  counsellorEmail: null,
  counsellorName: null,
  agentFamilyName: null,
  agentGivenName: null,
  branchAddressLine1: null,
  branchAddressLine2: null,
  branchAddressLine3: null,
  branchCity: null,
  branchCountry: null,
  branchPostcode: null,
} as const;

export const AGENT_DETAIL_FIELD_KEYS = Object.keys(CLEARED_AGENT_DETAIL_FIELDS) as Array<
  keyof typeof CLEARED_AGENT_DETAIL_FIELDS
>;

export const AGENT_DETAIL_FIELD_LABELS: Record<keyof typeof CLEARED_AGENT_DETAIL_FIELDS, string> = {
  agentFamilyName: 'Key Contact Family Name',
  agentGivenName: 'Key Contact Given Name',
  branchOffice: 'Branch Office',
  counsellorEmail: 'Counsellor Email',
  counsellorName: 'Counsellor Name',
  branchAddressLine1: 'Branch Address Line 1',
  branchAddressLine2: 'Branch Address Line 2',
  branchAddressLine3: 'Branch Address Line 3',
  branchCity: 'Branch City',
  branchCountry: 'Branch Country',
  branchPostcode: 'Branch Postcode',
};

export interface ISummaryIntakes {
  intakeId: string;
  offerId: string;
  casStatus: CoreStatusExtended;
  isActive: boolean;
  intakeLabel: string;
  startDate: string;
}

// used by getActiveOffers while populating the courses for all the active offers
export type IEnrichedOffer = Omit<IOffer, 'courses'> & {
  courses?: Array<IImportCourse>;
};

export * from './enums.js';
